import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { Observable, Subject, Subscription, buffer, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { County } from 'src/app/model/county';
import { Region } from 'src/app/model/region.model';
import { HelperService } from 'src/app/service/helper.service';
import { CountyEntityService } from '../../service/county-entity.service';

@Component({
    selector: 'county-dropdown-two-way',
    templateUrl: './county-dropdown-two-way.component.html',
    styleUrl: './county-dropdown-two-way.component.scss'
})
export class CountyDropdownTwoWayComponent implements OnInit {
    @Input() isMultiple: boolean = true;
    @Input() disableAutoComplete: boolean = false;
    @Input('reset') reset: boolean;
    @Input() required: boolean = false;
    @Input('disabled') disabled: boolean;
    @Input('customLabel') customLabel: string;

    @Input() selectedCounties: County[] = [];
    @Output() selectedCountiesChange = new EventEmitter<County[]>();

    @Input() selectedCounty: County;
    @Output() selectedCountyChange = new EventEmitter<County>();
    @Output() loadingChange = new EventEmitter<boolean>();

    @Input() regions: Region[] = [];

    @ViewChild('filterRef') filterRef: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSelect) matSelect: MatSelect;

    fieldLabel: string;
    countyForm: UntypedFormGroup;
    countyIds: number[] = [];
    selectedCountyIds: number[] = [];

    selectedCountyName: string | undefined;
    filteredCounties: County[] = [];
    filteredCountiesShow: County[] = [];

    // regions: Region[] = [];
    counties: County[] = [];
    allCounties: County[] = [];

    emitTimeout: any;
    timeout: any;
    loaded: boolean = false;
    loaded$: Observable<boolean>;
    isLoading: boolean = false;
    changed: boolean = true;

    backupScreenIds: number[] = [];

    //////

    subs: Subscription[] = [];

    pageIndex: number = 0;
    pageSize: number = 5;

    burstMS: number = 200;
    private changes$ = new Subject<any | null>();
    private destroy$ = new Subject<void>();
    private lastEmitted: any = undefined;
    private hasInitialized = false;

    constructor(
        private fb: UntypedFormBuilder,
        private countyService: CountyEntityService,
        private helperService: HelperService
    ) {
        this.countyForm = this.fb.group({
            countySelect: [''],
            filter: ['']
        });

        this.loaded$ = countyService.loaded$;
        let loadedSub = this.loaded$?.subscribe((loaded) => {
            if (!loaded || this.disabled) {
                this.countySelect.disable();
            } else {
                this.countySelect.enable();
            }
        });
        this.subs.push(loadedSub);

        const burstEnd$ = this.changes$.pipe(debounceTime(this.burstMS));

        // this.changes$.pipe(buffer(burstEnd$), takeUntil(this.destroy$)).subscribe((values) => {
        //     if (!values.length) return;

        //     //console.warn('[CountyInput] Burst buffer contents:', values);

        //     const preferred = [...values].reverse().find((v) => this.isNonEmpty(v)) ?? values[values.length - 1];

        //     //console.warn('[CountyInput] Chosen value:', preferred);

        //     this.emitNormalized(preferred);
        // });

        this.changes$.pipe(buffer(burstEnd$), takeUntil(this.destroy$)).subscribe((events) => {
            const list = Array.isArray(events) ? events : [events];

            let latestFilters: any = null;
            let latestSelection: any = null;
            let shouldReset = false;

            for (const e of list) {
                if (e?.type === 'filters') latestFilters = e;
                if (e?.type === 'selection') latestSelection = e.value;
                if (e?.type === 'reset') shouldReset = true;
            }

            if (shouldReset) {
                this.deSelectAll();
            }

            if (latestFilters) {
                this.selectedCountyIds = [];
                this.countySelect.patchValue([]);

                this.changed = true;
                this.refreshDataRequest();
            }

            if (latestSelection !== null) {
                this.emitNormalized(latestSelection);
            }
        });
    }

    private isNonEmpty(value: any): boolean {
        if (value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        return true;
    }

    private emitNormalized(value: any) {
        let normalized: number[];

        if (Array.isArray(value)) {
            normalized = value.map((r: any) => r.ID);
        } else if (value && typeof value === 'object') {
            normalized = [value.ID];
        } else if (value != null) {
            normalized = [value];
        } else {
            normalized = [];
        }

        if (this.isSame(normalized, this.lastEmitted)) return;

        this.lastEmitted = normalized;
        this.emit(normalized);
    }

    private isSame(a: any, b: any): boolean {
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            return a.every((v, i) => v === b[i]);
        }

        return a === b;
    }

    subscribers() {
        let countyEntitiesSub = this.countyService.entities$.subscribe((counties: County[]) => {
            if (counties && counties.length > 0) {
                this.allCounties = counties;
                this.countyIds = this.allCounties.length > 0 ? this.allCounties.map((county) => county.ID!) : [];

                this.changed = true;
                this.loaded = true;
                this.refreshDataRequest();
            }
        });
        this.subs.push(countyEntitiesSub);

        let filterInputSub = this.filterInput!.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value: string) => {
            if (value.length === 0) {
                this.filteredCounties = this.counties;
            } else {
                this.filteredCounties = this.counties.filter((county) => county.COUNTY_NAME!.toLowerCase().includes(value.toLowerCase()));
                this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            }
        });
        this.subs.push(filterInputSub);
    }

    emitSelect(countyIds: number[] | number) {
        const ids = Array.isArray(countyIds) ? countyIds : [countyIds];

        // BEFORE INITIAL LOAD: block empty
        if (!this.hasInitialized && (!ids || ids.length === 0)) {
            return;
        }

        // STILL block if data not ready
        if (!this.counties.length) {
            this.selectedCountyIds = ids; // preserve intent
            return;
        }

        this.selectedCountyIds = [...ids];

        const output = this.counties.filter((c) => ids.includes(c.ID));

        this.selectedCountyName = output[0]?.COUNTY_NAME;

        this.filterInput?.patchValue('');

        output[0] ? this.goToPage(output[0].ID!) : this.goToPage(0);

        this.selectedCountiesChange.emit(output);

        // AFTER INIT: allow empty emit
        this.selectedCountyChange.emit(output.length ? output[0] : undefined);
    }

    ngOnInit(): void {
        if (this.customLabel) {
            this.fieldLabel = this.customLabel;
        } else {
            this.fieldLabel = 'County';
        }

        this.subscribers();
    }

    ngOnDestroy() {
        this.subs.forEach((sub) => {
            sub.unsubscribe();
        });

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.disabled) {
            this.countySelect.disable();
        } else {
            this.countySelect.enable();
        }

        if (changes['reset'] && changes['reset'].currentValue) {
            this.changes$.next({ type: 'reset' });
        }

        if (changes['regions']) {
            this.changes$.next({
                type: 'filters',
                regions: this.regions
            });
        }

        if (changes['selectedCounties']) {
            this.changes$.next({
                type: 'selection',
                value: changes['selectedCounties'].currentValue
            });
            this.changes$.next(changes['selectedCounties'].currentValue);
        }

        if (changes['selectedCounty']?.currentValue) {
            this.changes$.next({
                type: 'selection',
                value: changes['selectedCounty'].currentValue
            });
            this.changes$.next(changes['selectedCounty'].currentValue);
        }
    }

    emit(ids: any) {
        const normalizedIds = Array.isArray(ids) ? ids : [ids];

        // Preserve selection even if data not ready
        this.selectedCountyIds = normalizedIds;

        if (!this.counties.length) return;

        this.select(normalizedIds);
        this.emitSelect(normalizedIds);
    }

    // refreshDataRequest() {
    //     //console.log("refreshDataRequest called")
    //     if (this.timeout) {
    //         clearTimeout(this.timeout);
    //     }

    //     this.timeout = setTimeout(() => {
    //         if (!this.loaded) {
    //             this.refreshDataRequest();
    //         } else {
    //             this.loadData(true);
    //         }
    //     }, 100);
    // }
    refreshDataRequest() {
        if (this.timeout) clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
            if (this.loaded) {
                this.loadData(true);
            }
        }, 100);
    }

    loadData(open: any) {
        if (open) {
            this.filterRef ? this.filterRef.nativeElement.focus() : null;
            if (!this.changed) return;

            this.counties = [];
            this.isLoading = true;
            this.loadingChange.emit(true);
            this.countySelect.disable();
            this.filterInput!.patchValue('');

            let workingCounties = this.allCounties;

            if (this.regions.length > 0) {
                workingCounties = workingCounties.filter((county) => this.regions.find((region) => region.ID === county.REGION?.ID));
            }

            this.counties = Array.from(new Set(workingCounties));
            this.counties.sort((a, b) => a.COUNTY_NAME!.localeCompare(b.COUNTY_NAME!));
            this.filteredCounties = this.counties;
            ////console.warn('2 - L - filteredCounties (' + this.filteredCounties.length + '/' + this.allCounties.length + ')');
            ////console.warn(this.regions, this.allCounties);

            ///console.log("loadData for counties called:")
            ///console.log(this.counties.length)
            ///console.log(this.selectedCountyIds);
            if (this.counties.length === 1 && !this.disableAutoComplete) {
                this.countySelect.patchValue([]);
                this.selectAll();
                this.emitSelect(this.selectedCountyIds);
            } else {
                this.select(this.selectedCountyIds);
                this.emitSelect(this.selectedCountyIds);
            }
            if (!this.disabled) {
                this.countySelect.enable();
            }
            if (!this.hasInitialized) {
                this.hasInitialized = true;
            }
            this.changed = false;
            this.isLoading = false;
            this.loadingChange.emit(false);
        } else {
            this.select(this.selectedCountyIds);
            this.emitSelect(this.selectedCountyIds);
        }
    }

    removeDuplicateIDs(arr: Array<any>): Array<any> {
        arr = arr.filter((value, index, self) => index === self.findIndex((t) => t.place === value.place && t.ID === value.ID));

        return arr;
    }

    select(selection: any) {
        // Get current page
        this.filteredCounties = this.counties;

        if (this.isMultiple) {
            // Remove all selections in current page
            let currentPageIds = this.filteredCountiesShow.map((show) => show.ID);
            this.selectedCountyIds = this.selectedCountyIds.filter((selectedCountyId) => !currentPageIds.includes(selectedCountyId));

            // Add current selection to total array
            this.selectedCountyIds = Array.from(new Set(this.selectedCountyIds.concat(selection)));

            // Remove all IDs that are not in available counties
            let filteredCounties = this.filteredCounties.map((filteredCounty) => filteredCounty.ID);
            this.selectedCountyIds = this.selectedCountyIds.filter((selectedCountyId) => filteredCounties.includes(selectedCountyId));

            this.countySelect.patchValue(this.selectedCountyIds);
        } else {
            if (Array.isArray(selection)) {
                this.countySelect.patchValue(selection[0]);
                this.selectedCountyIds = selection;
            } else {
                this.countySelect.patchValue(selection);
                this.selectedCountyIds = [selection];
            }

            this.goToPage(this.countySelect.value);
            //this.emitSelect(this.selectedCountyIds);
        }
    }

    selectAll() {
        ////console.log('calling selectAll');
        let selected = this.countySelect.value; // Currently selected
        if (this.isMultiple) {
            selected = Array.isArray(selected) ? selected : [];
            //Array.from(new Set((this.selectedCountyIds = selected.concat(this.filteredCounties.map((county) => county.ID!))))); // Select all filtered (if any filter)
            this.selectedCountyIds = Array.from(new Set(selected.concat(this.filteredCounties.map((c) => c.ID!))));
            this.countySelect.patchValue(this.selectedCountyIds);
        } else {
            this.selectedCountyIds = this.filteredCounties.map((county) => county.ID!);
            this.countySelect.patchValue(this.selectedCountyIds[0]);
        }
    }

    deSelectAll() {
        ////console.log('calling deSelectAll');
        //this.reset = false;
        this.countySelect.patchValue([]);
        this.selectedCountyIds = [];
        this.filterInput?.patchValue('');
        //this.emitSelect([]);
    }

    close() {
        this.matSelect.close();
    }

    get countySelect(): any {
        return this.countyForm.get('countySelect');
    }
    get filterInput(): AbstractControl | null {
        return this.countyForm.get('filter');
    }

    onPageChange($event: any) {
        $event.pageIndex = $event.pageIndex < 0 ? 0 : $event.pageIndex;

        this.pageIndex = $event.pageIndex;
        this.pageSize = $event.pageSize;

        this.filteredCountiesShow = this.filteredCounties.slice($event.pageIndex * $event.pageSize, $event.pageIndex * $event.pageSize + $event.pageSize);
    }

    goToPage(id: number) {
        let index = this.filteredCounties.findIndex((filteredCounty) => filteredCounty.ID === id);
        let pageIndex = Math.floor(index / this.pageSize);

        if (this.paginator) {
            this.paginator.pageIndex = pageIndex;
        }
        this.onPageChange({ pageIndex: pageIndex, pageSize: this.pageSize });
    }
}
